/* Copyright (c) 2020-present CloudIO Inc */

import useConstant from 'hooks/useConstant';

type Callback<T> = (element: T | null) => void;

export type IORef<T extends HTMLOrSVGElement> = Callback<T> & {
  subscribe: (cb: Callback<T>) => () => void;
  current: T | null;
  join: (ref: React.Ref<T>) => void;
  itemId?: string;
};

function setRef<E extends HTMLOrSVGElement>(
  ref: React.Ref<E> | undefined,
  value: E | null,
) {
  if (typeof ref === 'function') {
    ref(value);
    // eslint-disable-next-line no-prototype-builtins
  } else if (ref?.hasOwnProperty('current')) {
    // @ts-ignore ref is mutable
    ref.current = value;
  }
}

function createRef<T extends HTMLOrSVGElement>(
  initialValue?: T | null,
): IORef<T> {
  let el: T | null | undefined = initialValue;
  let itemId: string | undefined;
  const callbacks = new Set<Callback<T>>();
  const joinedRefs = new Set<React.Ref<T>>();

  const callbackRef = (element: T | null) => {
    if (el === element) return;
    el = element;
    joinedRefs.forEach((ref) => setRef(ref, element));
    callbacks.forEach((callback) => callback(element));
  };

  const subscribe = (callback: Callback<T>) => {
    callbacks.add(callback);
    if (el) {
      callback(el);
    }
    return () => callbacks.delete(callback);
  };

  const join = (ref: React.Ref<T>) => {
    joinedRefs.add(ref);
    if (el) {
      setRef(ref, el);
    }
  };

  const proxy = new Proxy(callbackRef, {
    apply(_target, thisArg, args) {
      return Reflect.apply(_target, thisArg, args);
    },
    get: (_target, key: string) => {
      if (key === 'subscribe') return subscribe;
      if (key === 'current') return el;
      if (key === 'apply') return callbackRef.apply;
      if (key === 'join') return join;
      if (key === 'itemId') return itemId;
      throw Error(`Invalid get to IORef.${key}!`);
    },
    set: (_target, key: string, value: T) => {
      if (key === 'current') {
        callbackRef(value);
        return true;
      } else if (key === 'itemId' && typeof value === 'string') {
        itemId = value;
        return true;
      }
      throw Error(`Invalid set of IORef.${key}!`);
    },
  }) as IORef<T>;
  return proxy;
}

export function useIORef<T extends HTMLOrSVGElement>(
  initialValue?: T | null,
): IORef<T> {
  return useConstant(() => createRef<T>(initialValue));
}
